import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import { useTranslations } from '@components/context/TranslationContext';
import useRovingFocus, { ROVING_ORIENTATION } from '@hooks/useRovingFocus';
import StepperSkeleton from './StepperSkeleton';
import {
    STEPPER_SKELETON_DEFAULT_STEPS,
    STEPPER_STEP_STATE,
    STEPPER_VARIANT,
    STEPPER_VARIANTS,
} from './constants';
import { normalizeSteps, stepState } from './helpers';

// Design System — Stepper
// Wizard navigation: icon boxes joined by a connector, labels below, the steps before the
// active one drawn as completed. `variant="compact"` is the icon-only vertical rail with
// tooltips. Controlled: `activeId` in, `onChange(id)` out. Invalid steps carry a badge and
// an sr-only text. `loading` renders the shimmer with the same geometry.
// Styles: ./index.less ships via less/ats_style.less (white-label pipeline).

const Stepper = ({
    steps: stepsProp = [],
    activeId,
    onChange = () => {},
    variant = STEPPER_VARIANT.FULL,
    disabled = false,
    loading = false,
    skeletonSteps,
    ariaLabel,
    labels = {},
    className,
    ...rest
}) => {
    const { t } = useTranslations();
    const safeVariant = STEPPER_VARIANTS.has(variant) ? variant : STEPPER_VARIANT.FULL;
    const steps = useMemo(() => normalizeSteps(stepsProp), [stepsProp]);
    const activeIndex = steps.findIndex((step) => step.id === activeId);
    const text = { invalid: labels.invalid || t('This section has errors') };

    const select = useCallback(
        (id) => {
            if (disabled || id === activeId) return;
            onChange(id);
        },
        [disabled, activeId, onChange],
    );

    const orientation = safeVariant === STEPPER_VARIANT.FULL ? ROVING_ORIENTATION.HORIZONTAL : ROVING_ORIENTATION.VERTICAL;

    // Manual activation: arrows only move focus; Enter/Space select.
    const { getItemRef, handleKeyDown, tabbableIndex } = useRovingFocus({
        count: steps.length,
        activeIndex,
        orientation,
        onActivate: (index) => select(steps[index].id),
    });

    if (loading) {
        return (
            <StepperSkeleton
                steps={skeletonSteps ?? (steps.length || STEPPER_SKELETON_DEFAULT_STEPS)}
                variant={safeVariant}
                className={className}
                {...rest}
            />
        );
    }

    return (
        <div
            role="tablist"
            aria-label={ariaLabel}
            aria-orientation={orientation}
            className={classNames('ds-stepper', `ds-stepper--${safeVariant}`, { 'ds-stepper--disabled': disabled }, className)}
            {...rest}
        >
            {steps.map((step, index) => {
                const state = stepState(index, activeIndex);
                const iconBox = (
                    <div className="ds-stepper__icon">
                        {step.icon && <Icon value={step.icon} noMargin />}
                        {step.invalid && <span className="ds-stepper__badge" aria-hidden="true" />}
                    </div>
                );
                return (
                    <a
                        key={step.id}
                        ref={getItemRef(index)}
                        href={`#${step.id}`}
                        role="tab"
                        data-item-id={step.id}
                        aria-selected={state === STEPPER_STEP_STATE.ACTIVE}
                        aria-invalid={step.invalid || undefined}
                        aria-disabled={disabled || undefined}
                        aria-label={safeVariant === STEPPER_VARIANT.COMPACT ? step.label : undefined}
                        tabIndex={index === tabbableIndex ? 0 : -1}
                        className={classNames('ds-stepper__step', `ds-stepper__step--${state}`, {
                            'ds-stepper__step--invalid': step.invalid,
                        })}
                        onClick={(event) => {
                            event.preventDefault();
                            select(step.id);
                        }}
                        onKeyDown={(event) => handleKeyDown(event, index)}
                    >
                        <div className="ds-stepper__content">
                            {safeVariant === STEPPER_VARIANT.COMPACT ? (
                                <Tooltip title={step.label}>{iconBox}</Tooltip>
                            ) : (
                                iconBox
                            )}
                            {safeVariant === STEPPER_VARIANT.FULL && (
                                <div className="ds-stepper__label">{step.label}</div>
                            )}
                            {step.invalid && <span className="ds-stepper__sr-only">{text.invalid}</span>}
                        </div>
                    </a>
                );
            })}
        </div>
    );
};

Stepper.propTypes = {
    /** Ordered steps: `{ id, label, icon (core/Icon name), invalid }` */
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.node,
            icon: PropTypes.string,
            invalid: PropTypes.bool,
        }),
    ),
    /** Id of the active step. Controlled. */
    activeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Called with the clicked step id (not called for the active step or while disabled) */
    onChange: PropTypes.func,
    /** 'full' (horizontal, labels) or 'compact' (vertical icon rail with tooltips) */
    variant: PropTypes.oneOf(Object.values(STEPPER_VARIANT)),
    /** Blocks navigation and dims the bar */
    disabled: PropTypes.bool,
    /** Renders the shimmer skeleton with the same geometry */
    loading: PropTypes.bool,
    /** Placeholder count for the skeleton (defaults to the step count, else 6) */
    skeletonSteps: PropTypes.number,
    /** Accessible name of the tablist */
    ariaLabel: PropTypes.string,
    /** Translated texts: `{ invalid }` — sr-only text on an invalid step */
    labels: PropTypes.shape({ invalid: PropTypes.string }),
    /** Extra class on the root */
    className: PropTypes.string,
};

export { StepperSkeleton, STEPPER_VARIANT, STEPPER_VARIANTS, STEPPER_STEP_STATE, normalizeSteps, stepState };
export default Stepper;
